import React, { useEffect, useState, useRef } from 'react';
import {
  BubbleInput,
  BubbleButton,
  BubbleDebounce,
  BubbleOverlay,
} from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import {
  VenueTemplateTransforms,
  VenueTemplateTranslatable,
  VenueTemplateTranslation,
} from '@go-shared/types/translation';
import { replaceTranslationByRefer } from '@go-client/lib/api/translation';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import { paginateVenueTemplates } from '@go-client/lib/api/venueTemplate';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import TranslationBlock from '@go-client/components/TranslationBlock';
import Translations from './Translations';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  CommonTranslationFilters,
  generateDefaultNewTranslation,
  makeVenueTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import { DEFAULT_PER_PAGE, PaginationMetadata } from '@go-shared/types/pagination';
import { PaginateTranslations } from '@go-client/components/Translations/PaginateTranslations';

interface VenueLibraryTranslationDataBundle {
  venueTemplates: VenueTemplate[];
}

interface VenueLibraryTranslationForm {
  dirty: boolean;
  transforms: VenueTemplateTransforms;
}

interface ChangeVenueTemplateFormAction {
  active?: boolean;
  valueChange?: { key: keyof VenueTemplateTranslatable; value: string };
}

type VenueLibraryTranslationsPageProps = RouteComponentProps;

export default function VenueLibraryTranslations(props: VenueLibraryTranslationsPageProps) {
  const { activeProperty } = useActiveProperty();
  const { languages } = useServerInfo();

  const [loading, setLoading] = useState(true);
  const [loadingTranslations, setLoadingTranslations] = useState(true);
  const [dataBundle, setDataBundle] = useState<VenueLibraryTranslationDataBundle | undefined>(
    undefined
  );
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>(
    undefined
  );
  const [activeVenTmpl, setActiveVenTmpl] = useState<VenueTemplate | undefined>(undefined);
  const [venTmplTranslForm, setVenTmplTranslForm] = useState<
    VenueLibraryTranslationForm | undefined
  >(undefined);

  const [paginationFilters, setPaginationFilters] = useState<CommonTranslationFilters>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    createdFrom: '',
    createdTo: '',
    propertyId: activeProperty._id,
    translated: undefined,
    published: undefined,
  });

  const contentRef = useRef<HTMLDivElement | null>(null);
  // this will scroll the page to top when search is modified
  useEffect(() => {
    // We want to scroll to top only when we are in page 1 or paginationParams are reset
    if (paginationFilters.page !== 1) {
      return;
    }

    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [paginationFilters, contentRef]);

  useEffect(() => {
    if (activeProperty._id !== paginationFilters.propertyId) {
      setPaginationFilters({ ...paginationFilters, propertyId: activeProperty._id });
    }
  }, [activeProperty]);

  async function refresh() {
    setLoadingTranslations(true);
    await fetchDataBundle();
  }

  // this will refresh the currently active venue template if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;
    setActiveVenTmpl(state => {
      const defaultVenTmpl = dataBundle.venueTemplates[0];
      if (!state) return defaultVenTmpl;
      const refreshedVenTmpl = dataBundle.venueTemplates.find(vt => vt._id === state._id);
      return refreshedVenTmpl || defaultVenTmpl;
    });
  }, [dataBundle]);

  useEffect(() => {
    refresh();
  }, [paginationFilters]);

  // this will reset the form each time a new venue template is clicked
  useEffect(() => {
    resetVenTmplForm();
  }, [activeVenTmpl]);

  async function fetchDataBundle() {
    try {
      const { meta, data } = await paginateVenueTemplates({
        propertyId: activeProperty._id,
        page: paginationFilters.page,
        translations: true,
        ...(paginationFilters.search && { search: paginationFilters.search }),
        ...(paginationFilters.createdFrom && { startDate: paginationFilters.createdFrom }),
        ...(paginationFilters.createdTo && { endDate: paginationFilters.createdTo }),
        ...(paginationFilters?.translated !== undefined && {
          translated: paginationFilters.translated,
        }),
        ...(paginationFilters?.published !== undefined && {
          published: paginationFilters.published,
        }),
      });

      setPaginationMetadata(meta);

      const venueTemplates =
        paginationFilters.page > 1 ? [...(dataBundle?.venueTemplates ?? []), ...data] : data;

      setDataBundle({
        venueTemplates,
      });
    } finally {
      setLoading(false);
      setLoadingTranslations(false);
    }
  }

  function resetVenTmplForm() {
    const transforms = activeVenTmpl
      ? makeVenueTranslationTransform(
          languages.map(l => l.key),
          activeVenTmpl?.translations
        )
      : undefined;

    if (!transforms) {
      setVenTmplTranslForm(undefined);
      return;
    }

    const formData: VenueLibraryTranslationForm = {
      dirty: false,
      transforms,
    };

    setVenTmplTranslForm(formData);
  }

  function changeVenTmplForm(lang: string, change: ChangeVenueTemplateFormAction) {
    if (!venTmplTranslForm) return;

    const updatedForm = { ...venTmplTranslForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setVenTmplTranslForm(updatedForm);
  }

  async function handleSubmitVenTmplForm() {
    if (!venTmplTranslForm || !activeVenTmpl || !dataBundle) return;

    const translation = generateDefaultNewTranslation(activeVenTmpl._id, 'venuetemplate');

    translation.transforms = venTmplTranslForm.transforms;

    const response = await replaceTranslationByRefer(activeVenTmpl._id, translation);
    const active = { ...activeVenTmpl, translations: response as VenueTemplateTranslation };
    const idx = dataBundle.venueTemplates.findIndex(vt => vt._id === active._id);
    dataBundle.venueTemplates[idx] = active;
    // No need to refresh bundle, just update the existing list
    setActiveVenTmpl(active);
    setDataBundle({ ...dataBundle });
  }

  function headerFn() {
    return <TranslationsSubNavigation active="venues" property={activeProperty} />;
  }

  function leftFn() {
    const venueTemplatesWithTranslations = dataBundle?.venueTemplates || [];
    const loadMoreTranslations =
      paginationMetadata && paginationMetadata.current_page < paginationMetadata.last_page;

    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleDebounce
            onTrigger={[
              (search: string) => setPaginationFilters({ ...paginationFilters, page: 1, search }),
              500,
            ]}
          >
            {({ debounce }) => (
              <BubbleInput
                type="text"
                block
                appearance="bare"
                placeholder="Search..."
                className="flex-grow"
                inputStyle={{ height: '49px' }}
                inputClassName="px-5"
                value={paginationFilters.search}
                onValue={debounce}
              />
            )}
          </BubbleDebounce>
        </div>
        <div style={{ paddingTop: '50px' }}>
          {venueTemplatesWithTranslations.map(vtmpl => (
            <div
              key={vtmpl._id}
              className={`list-item px-5 py-3 cursor-pointer ${
                activeVenTmpl && safeIdCompare(activeVenTmpl._id, vtmpl._id) ? 'active' : ''
              }`}
              onClick={() => setActiveVenTmpl(vtmpl)}
            >
              <div className="flex items-center">
                <div className="overflow-ellipsis">
                  <div className="list-item-name font-semibold">{vtmpl.name}</div>
                </div>
                <FontAwesomeIcon
                  icon="chevron-right"
                  className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                />
              </div>
            </div>
          ))}

          {venueTemplatesWithTranslations.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No venue templates found
            </div>
          )}

          {loadingTranslations && (
            <BubbleOverlay
              loading
              style={{ minHeight: '200px', marginTop: 'calc(40px + 0.75rem)' }}
              className="shadow rounded"
            />
          )}

          {loadMoreTranslations && (
            <PaginateTranslations
              type="Venues"
              onLoadMoreClick={() =>
                setPaginationFilters({
                  ...paginationFilters,
                  page: paginationFilters.page + 1,
                })
              }
            />
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeVenTmpl && venTmplTranslForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              <TranslationBlock
                label="Name"
                value={activeVenTmpl.name}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({venTmplTranslForm.transforms[language.key].delta.name.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !venTmplTranslForm.transforms[language.key].active ? 'smoke' : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeVenTmplForm(language.key, {
                          active: !venTmplTranslForm.transforms[language.key].active,
                        })
                      }
                      icon={venTmplTranslForm.transforms[language.key].active ? 'eye' : 'eye-slash'}
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !venTmplTranslForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={venTmplTranslForm.transforms[language.key].delta.name}
                      onValue={val =>
                        changeVenTmplForm(language.key, {
                          valueChange: { key: 'name', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            </div>
            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitVenTmplForm()}
                variant="navy"
                disabled={!venTmplTranslForm.dirty}
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!venTmplTranslForm.dirty}
                inverse
                onClick={() => resetVenTmplForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (venTmplTranslForm && venTmplTranslForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={venTmplTranslForm && venTmplTranslForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        category="venues"
        showLibraryFilters
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
        filters={paginationFilters}
        setFilters={setPaginationFilters}
      />
    </>
  );
}
